/**
 * Inspector: categorized sections for the selected timeline node.
 */
import React, { useMemo } from 'react';
import { INSPECTOR_SECTIONS, groupEventsBySection } from '@/utils/traceModel';
import { SmartDataRenderer } from './CollapsibleDataBlock';

const SECTION_ORDER = [
  'memory',
  'context',
  'llm',
  'tools',
  'final',
  'human',
  'asyncDoc',
  'routing',
  'other'
];

export default function TraceInspector({ node }) {
  const grouped = useMemo(() => {
    if (!node?.events?.length) return null;
    return groupEventsBySection(node.events);
  }, [node]);

  if (!node) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center text-gray-500 text-sm">
        <div>
          <p className="font-medium text-gray-700 mb-1">Select a step</p>
          <p className="text-xs text-gray-500">Click a node in the timeline to inspect structured events and payloads.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      <header className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">{node.title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {node.events.length} event{node.events.length === 1 ? '' : 's'} ·{' '}
          {node.firstTs && new Date(node.firstTs).toLocaleString()}
        </p>
      </header>

      {SECTION_ORDER.map((key) => {
        const events = grouped?.[key];
        if (!events?.length) return null;
        const meta = INSPECTOR_SECTIONS[key];
        return (
          <section key={key} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-800">
              {meta.label}
            </div>
            <div className="p-3 space-y-4">
              {events.map((ev) => (
                <article
                  key={ev.id}
                  className="rounded-lg border border-gray-100 bg-slate-50/50 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                      {ev.eventType || 'EVENT'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {ev.timestamp && new Date(ev.timestamp).toLocaleTimeString()}
                    </span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        (ev.level || '').toUpperCase() === 'ERROR'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {ev.level || 'INFO'}
                    </span>
                  </div>
                  {ev.message && (
                    <p className="text-sm text-gray-800 mb-2 leading-snug">{ev.message}</p>
                  )}
                  <SmartDataRenderer data={ev.data} eventLabel={ev.eventType || 'data'} />
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
